import type {
  FormSubmissionAddress,
  FormSubmissionContact,
  FormSubmissionCoordinates,
  FormSubmissionPerson,
  FormSubmissionSelect,
} from "../formSubmission";

export namespace ChangeOfOperatorApplication {
  //These are meant to map to the tags that we get back from the form submission.
  //The names of the tags are important and it's how we locate the data that we want.
  //If the tag is updated in nForm, it needs to be updated here as well.
  export const TAGS = {
    //facility section
    facilitySection_Tag: "facilitySection",
    facility_facilityName_Tag: "facilityName",
    facility_permitNumber_Tag: "permitNumber",
    facility_permitExpiration_Tag: "permitExpirationDate",
    facility_identificationNumber_Tag: "facilityID",
    // OperatorSection
    operatorSection_Tag: "operatorSection",
    operator_dateOfOperatorChange_Tag: "dateOfOperatorChange",
    operator_operatorOwnerAreTheSame_Tag: "Same_entity",
    operator_priorFacilityOperator_Tag: "priorFacilityOperatorName",
    operator_newFacOperator_Tag: "FacOper",
    //LocationSection
    locationSection_Tag: "locationSection",
    location_facilityCoordinates: "facilityCoordinates",
    location_quarterMileDrinkingWaterWell: "quarterMileDrinkingWaterWell",
    location_aboveBelowUicLine: "aboveBelowUicLine",
    location_taxMapKeyNumber_Tag: "taxMapKeyNumber",

    //OwnerSection
    ownerSection_Tag: "ownerSection",
    owner_priorFacOwner_Tag: "priorFacOwner",
    owner_newFacOwner_Tag: "FacOwn",

    //LegalContactSection
    legalContactSection_Tag: "legalContactSection",
    legal_priorLegalContact_Tag: "prevLegalContact",
    legal_newLegalContact_Tag: "LegalContact",

    //FeeSimpleLandOwnerSection
    feeSimpleLandOwnerSection_Tag: "feeSimpleLandOwnerSection",
    feeSimpleLandOwner_feeContact_Tag: "FeeContact",

    //InjectionWellOperationCharacteristicsSection
    injectionWellOperationCharacteristicsSection_Tag: "injectionWellOperationCharacteristicsSection",
    injectionWellOperationCharacteristics_injectionWellOperationCharacteristicChange_Tag:
      "injectionWellOperationCharacteristicChange",
    injectionWellOperationCharacteristics_injectionWellOperationCharacteristicDescription_Tag:
      "injectionWellOperationCharacteristicDescription",

    //ConsultantSection
    consultantSection_Tag: "consultantSection",
    consultant_consultant_Tag: "Consultant",

    //SpecialCommentsOrConsiderationsSection
    specialCommentsOrConsiderationsSection_Tag: "specialCommentsConsiderationsSection",
    specialCommentsOrConsiderations_affectPublicTrustNativeHawaiianResource_Tag:
      "affectPublicTrustNativeHawaiianResource",
    specialCommentsOrConsiderations_protectionFeasibleActions_Tag: "protectionFeasibleActions",
    specialCommentsOrConsiderations_additionalInformation_Tag: "additionalInformation",

    //SignatoryAndCertificationFormsSection
    // signatoryAndCertificationFormsSection_Tag: "signatoryCertificationFormSection",
    // signatoryAndCertificationForms_signatoryCertificationStatementUpload_Tag: "signatoryCertificationStatementUpload",
    // signatoryAndCertificationForms_landownerConsentFormUpload_Tag: "landownerConsentFormUpload",
  } as const;

  //ChangeOfOperatorApplication definition
  export interface Application {
    [sectionNames.facilityDetails]?: FacilityDetails;
    [sectionNames.operatorDetails]?: OperatorDetails;
    [sectionNames.locationDetails]?: LocationDetails;
    [sectionNames.ownerDetails]?: OwnerDetails;
    [sectionNames.legalContactDetails]?: LegalContactDetails;
    [sectionNames.feeSimpleLandOwnerDetails]?: FeeSimpleLandOwnerDetails;
    [sectionNames.injectionWellOperationCharacteristicsDetails]?: InjectionWellOperationCharacteristicsDetails;
    [sectionNames.consultantDetails]?: ConsultantDetails;
    [sectionNames.specialCommentsOrConsiderationsDetails]?: SpecialCommentsOrConsiderationsDetails;
    // [sectionNames.signatoryAndCertificationFormsDetails]?: SignatoryAndCertificationFormsDetails;
  }

  export interface FacilityDetails {
    [facilityPropertyNames.facilityName]?: string;
    [facilityPropertyNames.permitNumber]?: string;
    [facilityPropertyNames.permitExpirationDate]?: string;
    [facilityPropertyNames.facilityIdentificationNumber]?: string;
  }

  export interface OperatorDetails {
    [operatorPropertyNames.dateOfOperatorChange]?: string;
    [operatorPropertyNames.operatorOwnerAreTheSame]?: FormSubmissionSelect;
    [operatorPropertyNames.priorFacilityOperator]?: string;
    [operatorPropertyNames.newFacOperator]?: FormSubmissionContact;
  }

  export interface LocationDetails {
    [locationPropertyNames.facilityCoordinates]?: FormSubmissionCoordinates;
    [locationPropertyNames.quarterMileDrinkingWaterWell]?: FormSubmissionSelect;
    [locationPropertyNames.aboveBelowUicLine]?: FormSubmissionSelect;
    [locationPropertyNames.taxMapKeyNumber]?: string;
  }

  export interface OwnerDetails {
    [ownerPropertyNames.priorFacOwner]?: FormSubmissionContact;
    [ownerPropertyNames.newFacOwner]?: FormSubmissionContact;
  }

  export interface LegalContactDetails {
    [legalContactPropertyNames.priorLegalContact]?: FormSubmissionContact;
    [legalContactPropertyNames.newLegalContact]?: FormSubmissionContact;
  }

  export interface FeeSimpleLandOwnerDetails {
    [feeSimpleLandOwnerPropertyNames.feeContact]?: FormSubmissionContact;
  }
  export interface InjectionWellOperationCharacteristicsDetails {
    [injectionWellOperationCharacteristicsPropertyNames.injectionWellOperationCharacteristicChange]?: FormSubmissionSelect;
    [injectionWellOperationCharacteristicsPropertyNames.injectionWellOperationCharacteristicDescription]?: string;
  }
  export interface ConsultantDetails {
    [consultantPropertyNames.consultant]?: FormSubmissionContact;
  }
  export interface SpecialCommentsOrConsiderationsDetails {
    [specialCommentsOrConsiderationsPropertyNames.affectPublicTrustNativeHawaiianResource]?: FormSubmissionSelect;

    [specialCommentsOrConsiderationsPropertyNames.protectionFeasibleActions]?: string;
    [specialCommentsOrConsiderationsPropertyNames.additionalInformation]?: string;
  }
  // export interface SignatoryAndCertificationFormsDetails {
  //   [signatoryAndCertificationFormsPropertyNames.signatoryCertificationStatementUpload]?: any;
  //   [signatoryAndCertificationFormsPropertyNames.landownerConsentFormUpload]?: any;
  // }
  //Section and Property Names
  export const sectionNames = {
    facilityDetails: "facilityDetails",
    operatorDetails: "operatorDetails",
    locationDetails: "locationDetails",
    ownerDetails: "ownerDetails",
    legalContactDetails: "legalContactDetails",
    feeSimpleLandOwnerDetails: "feeSimpleLandOwnerDetails",
    injectionWellOperationCharacteristicsDetails: "injectionWellOperationCharacteristicsDetails",
    consultantDetails: "consultantDetails",
    specialCommentsOrConsiderationsDetails: "specialCommentsOrConsiderationsDetails",
    // signatoryAndCertificationFormsDetails: "signatoryAndCertificationFormsDetails",
  } as const;

  type SectionNames =
    | typeof sectionNames.facilityDetails
    | typeof sectionNames.operatorDetails
    | typeof sectionNames.locationDetails
    | typeof sectionNames.ownerDetails
    | typeof sectionNames.legalContactDetails
    | typeof sectionNames.feeSimpleLandOwnerDetails
    | typeof sectionNames.injectionWellOperationCharacteristicsDetails
    | typeof sectionNames.consultantDetails
    | typeof sectionNames.specialCommentsOrConsiderationsDetails;
  // | typeof sectionNames.signatoryAndCertificationFormsDetails;

  export const facilityPropertyNames = {
    facilityName: "facilityName",
    permitNumber: "permitNumber",
    permitExpirationDate: "permitExpirationDate",
    facilityIdentificationNumber: "facilityIdentificationNumber",
  } as const;

  export const operatorPropertyNames = {
    dateOfOperatorChange: "dateOfOperatorChange",
    operatorOwnerAreTheSame: "operatorOwnerAreTheSame",
    priorFacilityOperator: "priorFacilityOperator",
    newFacOperator: "newFacOperator",
  } as const;

  export const locationPropertyNames = {
    facilityCoordinates: "facilityCoordinates",
    quarterMileDrinkingWaterWell: "quarterMileDrinkingWaterWell",
    aboveBelowUicLine: "aboveBelowUicLine",
    taxMapKeyNumber: "taxMapKeyNumber",
  } as const;

  export const ownerPropertyNames = {
    priorFacOwner: "priorFacOwner",
    newFacOwner: "newFacOwner",
  } as const;

  export const legalContactPropertyNames = {
    priorLegalContact: "priorLegalContact",
    newLegalContact: "newLegalContact",
  } as const;

  export const feeSimpleLandOwnerPropertyNames = {
    feeContact: "feeContact",
  };

  export const injectionWellOperationCharacteristicsPropertyNames = {
    injectionWellOperationCharacteristicChange: "injectionWellOperationCharacteristicChange",
    injectionWellOperationCharacteristicDescription: "injectionWellOperationCharacteristicDescription",
  } as const;

  export const consultantPropertyNames = {
    consultant: "consultant",
  } as const;

  export const specialCommentsOrConsiderationsPropertyNames = {
    affectPublicTrustNativeHawaiianResource: "affectPublicTrustNativeHawaiianResource",
    protectionFeasibleActions: "protectionFeasibleActions",
    additionalInformation: "additionalInformation",
  } as const;

  // export const signatoryAndCertificationFormsPropertyNames = {
  //   signatoryCertificationStatementUpload: "signatoryCertificationStatementUpload",
  //   landownerConsentFormUpload: "landownerConsentFormUpload",
  // } as const;

  type PropertyNames =
    //facility
    | typeof facilityPropertyNames.facilityName
    | typeof facilityPropertyNames.permitNumber
    | typeof facilityPropertyNames.permitExpirationDate
    | typeof facilityPropertyNames.facilityIdentificationNumber
    //operator
    | typeof operatorPropertyNames.dateOfOperatorChange
    | typeof operatorPropertyNames.operatorOwnerAreTheSame
    | typeof operatorPropertyNames.priorFacilityOperator
    | typeof operatorPropertyNames.newFacOperator

    //location
    | typeof locationPropertyNames.facilityCoordinates
    | typeof locationPropertyNames.quarterMileDrinkingWaterWell
    | typeof locationPropertyNames.aboveBelowUicLine
    | typeof locationPropertyNames.taxMapKeyNumber

    //owner
    | typeof ownerPropertyNames.priorFacOwner
    | typeof ownerPropertyNames.newFacOwner
    //legalContact
    | typeof legalContactPropertyNames.priorLegalContact
    | typeof legalContactPropertyNames.newLegalContact

    //feeSimpleLandOwner
    | typeof feeSimpleLandOwnerPropertyNames.feeContact

    //injectionWellOperationCharacteristics
    | typeof injectionWellOperationCharacteristicsPropertyNames.injectionWellOperationCharacteristicChange
    | typeof injectionWellOperationCharacteristicsPropertyNames.injectionWellOperationCharacteristicDescription
    //Consultant
    | typeof consultantPropertyNames.consultant

    //SpecialCommentsOrConsiderations
    | typeof specialCommentsOrConsiderationsPropertyNames.affectPublicTrustNativeHawaiianResource
    | typeof specialCommentsOrConsiderationsPropertyNames.protectionFeasibleActions
    | typeof specialCommentsOrConsiderationsPropertyNames.additionalInformation;
  //SignatoryAndCertificationForms
  // | typeof signatoryAndCertificationFormsPropertyNames.signatoryCertificationStatementUpload
  // | typeof signatoryAndCertificationFormsPropertyNames.landownerConsentFormUpload;

  //FormSectionMapping definition
  export interface FormSectionMapping {
    sections: SectionMapping[];
  }

  export interface SectionMapping {
    sectionPropertyName: SectionNames;
    sectionTag: string;
    sectionProperties: SectionPropertyMapping[];
    sectionIsList: boolean; //Flag to tell the parsing function not to store the values as a list. All the sections come back as lists, but sometimes we only have one item. It will grab the first item in the list.
  }

  export interface SectionPropertyMapping {
    propertyName: PropertyNames;
    propertyTag: string;
  }

  //Actual mapping for Change Of Operator Application
  export const ChangeOfOperatorMapping: FormSectionMapping = {
    sections: [
      {
        sectionPropertyName: sectionNames.facilityDetails,
        sectionTag: TAGS.facilitySection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: facilityPropertyNames.facilityName,
            propertyTag: TAGS.facility_facilityName_Tag,
          },
          {
            propertyName: facilityPropertyNames.permitNumber,
            propertyTag: TAGS.facility_permitNumber_Tag,
          },
          {
            propertyName: facilityPropertyNames.permitExpirationDate,
            propertyTag: TAGS.facility_permitExpiration_Tag,
          },
          {
            propertyName: facilityPropertyNames.facilityIdentificationNumber,
            propertyTag: TAGS.facility_identificationNumber_Tag,
          },
        ],
      },

      {
        sectionPropertyName: sectionNames.operatorDetails,
        sectionTag: TAGS.operatorSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: operatorPropertyNames.dateOfOperatorChange,
            propertyTag: TAGS.operator_dateOfOperatorChange_Tag,
          },
          {
            propertyName: operatorPropertyNames.operatorOwnerAreTheSame,
            propertyTag: TAGS.operator_operatorOwnerAreTheSame_Tag,
          },
          {
            propertyName: operatorPropertyNames.priorFacilityOperator,
            propertyTag: TAGS.operator_priorFacilityOperator_Tag,
          },
          {
            propertyName: operatorPropertyNames.newFacOperator,
            propertyTag: TAGS.operator_newFacOperator_Tag,
          },
        ],
      },

      {
        sectionPropertyName: sectionNames.locationDetails,
        sectionTag: TAGS.locationSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: locationPropertyNames.facilityCoordinates,
            propertyTag: TAGS.location_facilityCoordinates,
          },
          {
            propertyName: locationPropertyNames.quarterMileDrinkingWaterWell,
            propertyTag: TAGS.location_quarterMileDrinkingWaterWell,
          },
          {
            propertyName: locationPropertyNames.aboveBelowUicLine,
            propertyTag: TAGS.location_aboveBelowUicLine,
          },
          {
            propertyName: locationPropertyNames.taxMapKeyNumber,
            propertyTag: TAGS.location_taxMapKeyNumber_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.ownerDetails,
        sectionTag: TAGS.ownerSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: ownerPropertyNames.priorFacOwner,
            propertyTag: TAGS.owner_priorFacOwner_Tag,
          },
          {
            propertyName: ownerPropertyNames.newFacOwner,
            propertyTag: TAGS.owner_newFacOwner_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.legalContactDetails,
        sectionTag: TAGS.legalContactSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: legalContactPropertyNames.priorLegalContact,
            propertyTag: TAGS.legal_priorLegalContact_Tag,
          },
          {
            propertyName: legalContactPropertyNames.newLegalContact,
            propertyTag: TAGS.legal_newLegalContact_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.feeSimpleLandOwnerDetails,
        sectionTag: TAGS.feeSimpleLandOwnerSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: feeSimpleLandOwnerPropertyNames.feeContact,
            propertyTag: TAGS.feeSimpleLandOwner_feeContact_Tag,
          },
        ],
      },
      {
        sectionPropertyName: sectionNames.injectionWellOperationCharacteristicsDetails,
        sectionTag: TAGS.injectionWellOperationCharacteristicsSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: injectionWellOperationCharacteristicsPropertyNames.injectionWellOperationCharacteristicChange,
            propertyTag: TAGS.injectionWellOperationCharacteristics_injectionWellOperationCharacteristicChange_Tag,
          },
          {
            propertyName:
              injectionWellOperationCharacteristicsPropertyNames.injectionWellOperationCharacteristicDescription,
            propertyTag: TAGS.injectionWellOperationCharacteristics_injectionWellOperationCharacteristicDescription_Tag,
          },
        ],
      },

      {
        sectionPropertyName: sectionNames.consultantDetails,
        sectionTag: TAGS.consultantSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: consultantPropertyNames.consultant,
            propertyTag: TAGS.consultant_consultant_Tag,
          },
        ],
      },

      {
        sectionPropertyName: sectionNames.specialCommentsOrConsiderationsDetails,
        sectionTag: TAGS.specialCommentsOrConsiderationsSection_Tag,
        sectionIsList: false,
        sectionProperties: [
          {
            propertyName: specialCommentsOrConsiderationsPropertyNames.affectPublicTrustNativeHawaiianResource,
            propertyTag: TAGS.specialCommentsOrConsiderations_affectPublicTrustNativeHawaiianResource_Tag,
          },
          {
            propertyName: specialCommentsOrConsiderationsPropertyNames.protectionFeasibleActions,
            propertyTag: TAGS.specialCommentsOrConsiderations_protectionFeasibleActions_Tag,
          },
          {
            propertyName: specialCommentsOrConsiderationsPropertyNames.additionalInformation,
            propertyTag: TAGS.specialCommentsOrConsiderations_additionalInformation_Tag,
          },
        ],
      },

      // {
      //   sectionPropertyName: sectionNames.signatoryAndCertificationFormsDetails,
      //   sectionTag: TAGS.signatoryAndCertificationFormsSection_Tag,
      //   sectionIsList: false,
      //   sectionProperties: [
      //     {
      //       propertyName: signatoryAndCertificationFormsPropertyNames.signatoryCertificationStatementUpload,
      //       propertyTag: TAGS.signatoryAndCertificationForms_signatoryCertificationStatementUpload_Tag,
      //     },
      //     {
      //       propertyName: signatoryAndCertificationFormsPropertyNames.landownerConsentFormUpload,
      //       propertyTag: TAGS.signatoryAndCertificationForms_landownerConsentFormUpload_Tag,
      //     },
      //   ],
      // },
    ],
  };
}
